<template>
    <div class="modal-fade" :class="{ 'is-active': active }">
        <div class="modal is-active" :class="classes">
            <div class="modal-background"></div>

            <div v-if="form && form.successful" class="modal-card">
                <section class="modal-card-body">
                    <modal-alert-success>{{ successText }}</modal-alert-success>
                </section>
            </div>

            <div v-else-if="showError" class="modal-card">
                <section class="modal-card-body">
                    <modal-alert-error>{{ errorText }}</modal-alert-error>
                </section>
            </div>

            <form v-else class="modal-card" @submit.prevent="submit">
                <header class="modal-card-head">
                    <p class="modal-card-title">
                        <fa-icon v-if="titleIcon" :icon="titleIcon" /> <span v-text="title" />
                        <span v-if="subtitle" class="modal-card-subtitle" v-text="subtitle" />
                    </p>
                    <button
                        type="button"
                        class="delete"
                        :disabled="form && form.busy"
                        @click="closeModal()"
                    />
                </header>

                <div v-if="showProgressBar" class="progress-bar">
                    <div class="progress" :style="{ width: progress + '%' }" />
                </div>

                <section class="modal-card-body">
                    <slot>No modal content provided.</slot>
                </section>

                <footer
                    v-if="!hideFoot"
                    class="modal-card-foot"
                    :class="{ 'justify-space-between': buttonsFlexSpaceBetween }"
                >
                    <slot name="close">
                        <button
                            v-if="!hideCancel"
                            type="button"
                            class="button"
                            :class="compiledCancelClasses"
                            :disabled="(form && form.busy) || (formOther && formOther.busy)"
                            @click="closeModal()"
                            v-text="cancelText"
                        />
                    </slot>
                    <slot name="back">
                        <button
                            v-if="!hideBack"
                            type="button"
                            class="button"
                            :class="compiledBackClasses"
                            :disabled="backDisabled"
                            @click="
                                () => {
                                    if (!backDisabled) handleBack();
                                }
                            "
                            v-text="backText"
                        />
                    </slot>
                    <slot name="next">
                        <button
                            v-if="!hideNext"
                            class="button"
                            :class="compiledNextClasses"
                            :disabled="nextDisabled"
                            @click="handleNext()"
                            v-text="nextText"
                        />
                    </slot>
                    <slot name="action">
                        <button
                            v-if="!hideAction"
                            class="button"
                            :class="compiledActionClasses"
                            :disabled="actionDisabled"
                            @click="handleAction()"
                            v-text="actionText"
                        />
                    </slot>
                    <slot name="finish">
                        <button
                            v-if="!hideFinish"
                            class="button"
                            :type="finishSubmitsForm ? 'submit' : 'button'"
                            :class="compiledFinishClasses"
                            :disabled="
                                finishDisabled ||
                                (form && form.busy) ||
                                (formOther && formOther.busy)
                            "
                            @click="handleFinish()"
                            v-text="finishText"
                        />
                    </slot>
                </footer>
            </form>
        </div>
    </div>
</template>

<script>
export default {
    emits: [
        'closed',
        'submit',
        'handleBack',
        'handleNext',
        'handleAction',
        'handleFinish',
        'opened',
    ],

    props: {
        type: { type: String, default: '' },
        finishSubmitsForm: { type: Boolean, default: false },
        form: { type: Object, default: null },
        formOther: { type: Object, default: null },
        confirm: { type: Boolean, default: false },
        size: { type: String, default: '' },
        title: { type: String, default: 'No Modal Title' },
        subtitle: { type: String, default: '' },
        titleIcon: { type: String, default: '' },
        successText: { type: String, default: 'Success!' },
        closeOnSuccess: { type: Boolean, default: true },
        hideFoot: { type: Boolean, default: false },
        showError: { type: Boolean, default: false },
        errorText: { type: String, default: '' },

        hideCancel: { type: Boolean, default: true },
        cancelDisabled: { type: Boolean, default: false },
        cancelText: { type: String, default: 'Cancel' },
        cancelClasses: { type: [Array, String, Object], default: null },

        hideSubmit: { type: Boolean, default: true },
        submitDisabled: { type: Boolean, default: false },
        submitText: { type: String, default: 'Submit' },
        submitClasses: { type: [Array, String, Object], default: null },

        hideBack: { type: Boolean, default: false },
        backDisabled: { type: Boolean, default: false },
        backText: { type: String, default: 'Back' },
        backClasses: { type: [Array, String, Object], default: null },

        hideNext: { type: Boolean, default: false },
        nextDisabled: { type: Boolean, default: false },
        nextText: { type: String, default: 'Next' },
        nextClasses: { type: [Array, String, Object], default: null },

        hideAction: { type: Boolean, default: true },
        actionDisabled: { type: Boolean, default: false },
        actionText: { type: String, default: 'Apply' },
        actionClasses: { type: [Array, String, Object], default: null },

        hideFinish: { type: Boolean, default: false },
        finishDisabled: { type: Boolean, default: false },
        finishText: { type: String, default: 'Done' },
        finishClasses: { type: [Array, String, Object], default: null },

        currentStep: { type: Number, default: 1 },
        maxStep: { type: [Number, Function], default: 1 },
        stepsValid: { type: Object, default: null },

        hideFirstBack: { type: Boolean, default: false },
        buttonsFlexSpaceBetween: { type: Boolean, default: false },
        steps: { type: Object, default: null },
        showProgressBar: { type: Boolean, default: false },
        progress: { type: Number, default: 0 },
        noStepsValidation: { type: Boolean, default: false },
    },

    data() {
        return {
            active: false,
            closeTimeout: null,
        };
    },

    computed: {
        /**
         * Returns all of the classes for the modal.
         *
         * @returns Array
         */
        classes() {
            let classes = [
                this.size,
                this.confirm ? 'is-confirm' : '',
                this.hideFoot ? 'is-footless' : '',
            ];

            if (this.form && this.form.successful) {
                classes.push('is-successful');
            } else if (!this.showError) {
                classes.push(this.type);
            }

            return classes;
        },

        /**
         * Returns the classes for the cancel button.
         *
         * @return {*}
         */
        compiledCancelClasses() {
            if (this.cancelClasses) {
                return this.cancelClasses;
            } else {
                return {
                    // 'is-fullwidth': this.hideSubmit
                };
            }
        },

        compiledBackClasses() {
            if (this.backClasses) {
                return this.backClasses;
            } else {
                return {};
            }
        },

        compiledNextClasses() {
            if (this.nextClasses) {
                return this.nextClasses;
            } else {
                return {};
            }
        },

        compiledActionClasses() {
            if (this.actionClasses) {
                return this.actionClasses;
            } else {
                return {};
            }
        },

        compiledFinishClasses() {
            const classes = [];

            if (this.finishClasses) {
                classes.push(this.finishClasses);
            }

            if ((this.form && this.form.busy) || (this.formOther && this.formOther.busy)) {
                classes.push('is-busy');
            }

            return classes.join(' ');
        },
    },

    watch: {
        /**
         * Watches the successful state and automatically closes the modal.
         *
         * @param {Boolean} val
         * @return {void}
         */
        'form.successful'(val) {
            if (val && this.closeOnSuccess) {
                this.closeTimeout = setTimeout(() => {
                    this.closeModal();
                }, 1500);
            } else {
                clearTimeout(this.closeTimeout);
            }
        },
    },

    methods: {
        /**
         * Closes the modal.
         *
         * @return {void}
         */
        closeModal() {
            this.active = false;

            setTimeout(() => {
                this.$emit('closed');
            }, 201);
        },

        /**
         * Handles the modal submit procedure.
         *
         * @return {void}
         */
        submit() {
            this.$emit('submit');
        },

        handleBack() {
            this.$emit('handleBack');
        },

        handleNext() {
            this.$emit('handleNext');
        },

        handleAction() {
            this.$emit('handleAction');
        },

        handleFinish() {
            if (!this.finishSubmitsForm) {
                this.closeModal();
                this.$emit('handleFinish');
            }
        },
    },

    mounted() {
        setTimeout(() => {
            this.active = true;
            this.$emit('opened');
        }, 10);
    },
};
</script>

<style lang="scss">
.modal-card-foot.justify-space-between {
    justify-content: space-between;
}

.modal-card-subtitle {
    display: block;
    font-size: 1rem;
    padding-top: 5px;
    color: #9b9b9b;
}

.space-between-hidden {
    opacity: 0;
    width: 0;
    height: 0;
    overflow: hidden;
}

.progress-bar {
    width: 100%;
    height: 8px;
    position: relative;
    background-color: #dbdbdb;
}

.progress {
    position: absolute;
    height: 8px;
    left: 0;
    top: 0;
    background-color: #2693f8;
}
</style>
